import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, onValue, off, getDatabase } from 'firebase/database';
import app from '../config/firebase';
import './LobbyPage.css';

const database = getDatabase(app);

function LobbyPage({ API_URL }) {
  const navigate = useNavigate();
  const { code } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [partyInfo, setPartyInfo] = useState({
    name: '',
    players: [],
    type: 'classic',
    offensiveLevel: 'mild',
    code: code
  });

  const [isHost, setIsHost] = useState(false);
  
  // Toast notification state
  const [toast, setToast] = useState({
    visible: false,
    message: '',
    type: 'success' // 'success', 'error', 'info'
  });
  const toastTimeoutRef = useRef(null);

  // Debug localStorage values
  useEffect(() => {
    console.log('localStorage values:', {
      playerId: localStorage.getItem('playerId'),
      isHost: isHost,
      playerName: localStorage.getItem('playerName'),
      partyCode: localStorage.getItem('partyCode')
    });
  }, []);

  // Listen for real-time updates
  useEffect(() => {
    const partyRef = ref(database, `parties/${code}`);
    
    // Set up real-time listener
    onValue(partyRef, (snapshot) => {
      const data = snapshot.val();
      console.log('Party data:', data);
      
      if (!data) {
        setError('Party not found');
        setIsLoading(false);
        return;
      }

      // Check game state
      if (data.gameFinished) {
        navigate(`/analysis/${code}`);
        return;
      }
      
      if (data.gameStarted && !data.gameFinished) {
        // Direct users to the appropriate game page based on party type
        if (data.info?.type === 'acordingto') {
          navigate(`/accordingto/${code}`);
        } else {
          // Default to regular game page for 'wiml' and 'icebreaker' types
          navigate(`/game/${code}`);
        }
        return;
      }

      // Map players with their IDs and host status
      const players = Object.entries(data.players || {}).map(([playerId, player]) => ({
        id: playerId,
        name: player.name,
        isHost: player.isHost === true
      }));

      setPartyInfo({
        name: data.info?.name || 'Party Name',
        players,
        type: data.info?.type || 'classic',
        offensiveLevel: data.info?.offensiveLevel || 'mild',
        code: code
      });
      setIsHost(data.info?.hostId === localStorage.getItem('playerId'));
      setError(null);
      setIsLoading(false);
    }, (error) => {
      console.error('Firebase error:', error);
      setError('Failed to load party data');
      setIsLoading(false);
    });

    // Clean up listener when component unmounts
    return () => {
      off(partyRef);
    };
  }, [code, navigate]);

  const showToast = (message, type = 'success', duration = 3000) => {
    // Clear any existing timeout
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    // Show the toast
    setToast({
      visible: true,
      message,
      type
    });
    
    // Hide the toast after specified duration (default 3 seconds)
    toastTimeoutRef.current = setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, duration);
  };
  
  // Clean up toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const copyPartyCode = () => {
    // Modern browsers support
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code)
        .then(() => {
          showToast('Party code copied to clipboard!', 'success');
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
          fallbackCopyTextToClipboard();
        });
    } else {
      // Fallback for browsers that don't support the Clipboard API (like iOS Safari)
      fallbackCopyTextToClipboard();
    }
  };

  // Fallback method to copy text using document.execCommand
  const fallbackCopyTextToClipboard = () => {
    try {
      // Check if we're on iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      
      if (isIOS) {
        // iOS-specific approach with better UX
        // Show the code with a special modal or highlight it prominently
        showToast(`Your party code is: ${code}`, 'info', 6000); // Show longer on iOS
        
        // Create a visible, selectable element for the user
        const codeElement = document.querySelector('.party-code');
        if (codeElement) {
          // Apply a highlight animation
          codeElement.style.animation = 'highlight-pulse 2s ease-in-out';
          
          // Reset animation after it completes
          setTimeout(() => {
            codeElement.style.animation = '';
          }, 2000);
        }
        
        return;
      }
      
      // Non-iOS fallback using execCommand
      const textArea = document.createElement('textarea');
      textArea.value = code;
      
      // Make the textarea out of viewport but still accessible
      textArea.style.position = 'fixed';
      textArea.style.left = '0';
      textArea.style.top = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      // Execute the copy command
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        showToast('Party code copied to clipboard!', 'success');
      } else {
        showToast(`Party code: ${code}`, 'info');
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      showToast(`Party code: ${code}`, 'info');
    }
  };

  const startGame = async () => {
    if (!isHost) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/parties/${code}/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Redirect to the appropriate game page based on party type
      if (partyInfo.type === 'acordingto') {
        navigate(`/accordingto/${code}`);
      } else {
        // Default to regular game page for 'wiml' and 'icebreaker' types
        navigate(`/game/${code}`);
      }
    } catch (err) {
      console.error('Error starting game:', err);
      setError('Failed to start game. Please try again.');
      showToast('Failed to start game. Please try again.', 'error');
    }
  };

  const getGameTypeName = (type) => {
    const types = {
      'wiml': 'Who Is Most Likely',
      'icebreaker': 'Ice Breaker',
      'acordingto': 'According To'
    };
    return types[type] || type;
  };

  const getPlayerInitials = (name) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="lobby-container">
        <div className="lobby-card">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lobby-container">
        <div className="lobby-card">
          <div className="error-message">{error}</div>
          <button className="retry-button" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lobby-container">
      {/* Toast notification */}
      {toast.visible && (
        <div className={`toast-notification ${toast.type}`}>
          <div className="toast-content">
            {toast.type === 'success' && <span className="toast-icon">✓</span>}
            {toast.type === 'error' && <span className="toast-icon">✗</span>}
            {toast.type === 'info' && <span className="toast-icon">i</span>}
            <span>{toast.message}</span>
          </div>
        </div>
      )}
      
      <div className="lobby-card">
        <div className="party-info">
          <h1 className="title">{partyInfo.name}</h1>
          <div>
            <span className="game-type">{getGameTypeName(partyInfo.type)}</span>
            <span className="offensive-level">
              {partyInfo.offensiveLevel === 'mild' && '😊'}
              {partyInfo.offensiveLevel === 'medium' && '😈'}
              {partyInfo.offensiveLevel === 'spicy' && '🔥'}
              {partyInfo.offensiveLevel.charAt(0).toUpperCase() + partyInfo.offensiveLevel.slice(1)}
            </span>
          </div>
          <div className="party-code">{partyInfo.code}</div>
          <button className="copy-button" onClick={copyPartyCode}>
            Copy Party Code
          </button>
        </div>

        <h2 className="subtitle">Players ({partyInfo.players.length})</h2>
        <div className="players-list">
          {partyInfo.players.map(player => (
            <div key={player.id} className="player-item">
              <div className="player-avatar">
                {getPlayerInitials(player.name)}
              </div>
              <span className="player-lobby-name">{player.name}</span>
              {player.isHost && (
                <span className="host-badge">Host</span>
              )}
            </div>
          ))}
        </div>

        {(() => {
          return (isHost) && (
          <button
            className="start-button"
            onClick={startGame}
            disabled={partyInfo.players.length < 2}
          >
            {partyInfo.players.length < 2 
              ? 'Waiting for more players...'
              : 'Start Game'}
          </button>
        )})()}
      </div>
    </div>
  );
}

export default LobbyPage;
